import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ImageOff } from 'lucide-react';
import type { Media } from '@/types';
import {
  responsiveImageSource,
  safeImageUrl,
  type ImageTransform,
  type ResponsiveImageSource,
} from '@/utils/safeImageUrl';

export interface ImgProps {
  media?: Media | null;
  transform?: ImageTransform;
  alt: string;
  className?: string;
  /** Rendered centered when the image is missing or fails to load. */
  fallback?: ReactNode;
  fit?: 'cover' | 'contain';
  /**
   * The slot's rendered width, as an `<img sizes>` value (e.g.
   * CONTENT_COLUMN_SIZES). Setting it renders a `srcset` over the fixed
   * SRCSET_WIDTHS ladder and lets the browser choose; without it the image
   * gets a single `src` sized from `transform` — right for small fixed boxes.
   */
  sizes?: string;
  /**
   * Candidate widths for that srcset: SRCSET_WIDTHS by default (fluid images);
   * small fixed boxes pass THUMB_SRCSET_WIDTHS. Ignored without `sizes`. Pass
   * a module constant, not an inline array (it is a memo dependency).
   */
  widths?: readonly number[];
  /**
   * This image is the page's likely LCP element (home hero slide 0, flyer
   * page 1, detail cover and logo): load it eagerly at high fetch priority and
   * paint it the moment bytes arrive instead of fading it in after `load`.
   */
  priority?: boolean;
  /** Called once the image has loaded or failed — never for a missing image. */
  onSettled?: () => void;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/** Intrinsic size for the width/height attributes. It describes `url[0]` only. */
function intrinsicSize(media?: Media | null): { width?: number; height?: number } {
  const { width, height } = media ?? {};
  return typeof width === 'number' && width > 0 && typeof height === 'number' && height > 0
    ? { width, height }
    : {};
}

/**
 * Media image with shimmer-while-loading and graceful fallback. Lazy unless
 * `priority`. Resolves the URL through safeImageUrl (imgproxy transforms, host
 * rewrites), or a srcset ladder when `sizes` is given.
 */
export function Img({
  media,
  transform,
  alt,
  className,
  fallback,
  fit = 'cover',
  sizes,
  widths,
  priority = false,
  onSettled,
}: ImgProps) {
  const source = useMemo<ResponsiveImageSource | null>(() => {
    if (sizes) return responsiveImageSource(media ?? null, transform, widths);
    const src = safeImageUrl(media ?? null, transform);
    return src ? { src } : null;
  }, [media, transform, sizes, widths]);
  const url = source?.src ?? null;
  const { width, height } = intrinsicSize(media);

  // The status belongs to the URL it was reported for: a new URL (entity swap in
  // recycled lists) starts as 'loading' again without an effect that could undo
  // a status the ref callback below has just settled.
  const [settled, setSettled] = useState<{ url: string | null; status: 'loaded' | 'error' } | null>(null);
  const status: 'loading' | 'loaded' | 'error' = settled && settled.url === url ? settled.status : 'loading';
  const setStatus = useCallback((next: 'loaded' | 'error') => setSettled({ url, status: next }), [url]);

  // Reported from an effect on `status`, so a cached image that completed
  // before its handlers attached (see handleImgRef) settles too.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  useEffect(() => {
    if (status !== 'loading') onSettledRef.current?.();
  }, [status]);

  // Images can settle before React attaches onLoad / onError (a cached one, or
  // any image of the server HTML before hydration): a finished one with pixels
  // is loaded, a finished one without (a 404) failed, so it shows its fallback
  // instead of a broken-image box and a shimmer that never ends.
  const handleImgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (!node || !node.complete) return;
      setStatus(node.naturalWidth > 0 ? 'loaded' : 'error');
    },
    [setStatus],
  );

  const showImage = Boolean(url) && status !== 'error';

  const shimmer = status === 'loading' && (
    <div aria-hidden="true" className="absolute inset-0 animate-pulse bg-skeleton" />
  );

  return (
    <div className={cn('relative overflow-hidden bg-line-light', className)}>
      {showImage ? (
        <>
          {/* A priority image paints over its shimmer as bytes arrive (both
              positioned, so DOM order decides), and never sits at opacity 0:
              an invisible image is not an LCP candidate, so fading it in only
              pushes LCP past the fade. */}
          {priority && shimmer}
          <img
            key={url ?? undefined}
            ref={handleImgRef}
            src={url ?? undefined}
            srcSet={source?.srcSet}
            sizes={source?.srcSet ? sizes : undefined}
            width={width}
            height={height}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            decoding={priority ? undefined : 'async'}
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('error')}
            className={cn(
              'h-full w-full',
              fit === 'contain' ? 'object-contain' : 'object-cover',
              priority
                ? 'relative'
                : cn(
                    'transition-opacity duration-300',
                    status === 'loaded' ? 'opacity-100' : 'opacity-0',
                  ),
            )}
          />
          {!priority && shimmer}
        </>
      ) : (
        // Out of flow, filling the same box the image had (the root is `relative`
          // and sized by its caller): swapping a failed image for this fallback
          // moves nothing around it (audit F2: CLS 0.19 on /brands).
          <div
            data-img-fallback=""
            className="absolute inset-0 flex items-center justify-center"
          >
          {fallback ?? (
            <ImageOff size={28} className="text-ink-tertiary" aria-hidden="true" />
          )}
          </div>
      )}
    </div>
  );
}
